/**
 * The score: one quiet, endless bed of sound per part of the property.
 *
 * Synthesised like everything else, at runtime, out of oscillators and a
 * filter, so there is no file to load and nothing to loop audibly. Each zone
 * is a chord, a register and a pace: back of house is low and slow, the lobby
 * and ballroom are warmer and higher, outdoors is open fifths with a lot of
 * air. Every few seconds one voice of the chord swells and fades, and now and
 * then a single soft note is picked out above it. Moving between zones
 * crossfades over a few seconds.
 *
 * It must never cover anything the player needs to hear. The game's sounds
 * are how you know somebody is coming, what went over, where the herd is: they
 * are the same things perception hears. So the score is kept out of their way
 * three times over: it is quiet (its own fader, default 35% of master), it is
 * low-passed under 1.1 kHz so it sits beneath the clicks, steps and voices the
 * world makes, and it ducks by half whenever a world sound plays (`duck()`).
 */

/** Hz for a semitone offset from A2. */
const note = (semis) => 110 * 2 ** (semis / 12);

/**
 * Per zone: the chord (semitones from A2), how often a voice swells (seconds),
 * how bright the filter is, and how often a single note is picked out.
 */
export const SCORE = Object.freeze({
  backOfHouse: { chord: [0, 7, 12, 15], every: 7, bright: 650, pick: 0.15 },
  frontOfHouse: { chord: [3, 10, 15, 19, 22], every: 6, bright: 950, pick: 0.3 },
  ballroom: { chord: [5, 12, 17, 21, 24], every: 6, bright: 1000, pick: 0.35 },
  outdoors: { chord: [-5, 2, 7, 14], every: 9, bright: 800, pick: 0.2 },
  night: { chord: [-2, 5, 10, 13], every: 10, bright: 520, pick: 0.1 },
});

/** Which score a place and an hour gets. Pure, so it can be tested. */
export function scoreFor({ zone = 'backOfHouse', outdoors = false, night = false } = {}) {
  if (outdoors) return night ? 'night' : 'outdoors';
  if (zone === 'ballroom') return 'ballroom';
  if (zone === 'frontOfHouse') return 'frontOfHouse';
  return 'backOfHouse';
}

export function createMusic({ context, destination, random = Math.random }) {
  if (!context || !destination) return null;

  // Everything goes through a filter and a ducking gain before the music fader.
  const filter = context.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 800;
  filter.Q.value = 0.4;
  const duckGain = context.createGain();
  duckGain.gain.value = 1;
  filter.connect(duckGain);
  duckGain.connect(destination);

  let current = null;
  let nextSwell = 0;
  let nextPick = 0;
  /** The held drone: one quiet sine per chord tone, faded in under the swells. */
  let drone = [];

  function stopDrone(at) {
    for (const voice of drone) {
      voice.gain.gain.cancelScheduledValues(at);
      voice.gain.gain.setTargetAtTime(0, at, 1.2);
      voice.osc.stop(at + 6);
    }
    drone = [];
  }

  function startDrone(score, at) {
    for (const semis of score.chord.slice(0, 3)) {
      const osc = context.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = note(semis);
      // A very slow beat between voices, so the bed breathes rather than hums.
      osc.detune.value = (random() - 0.5) * 6;
      const gain = context.createGain();
      gain.gain.value = 0;
      gain.gain.setTargetAtTime(0.05, at, 2.5);
      osc.connect(gain);
      gain.connect(filter);
      osc.start(at);
      drone.push({ osc, gain });
    }
  }

  /** One voice of the chord swelling up and away, a few seconds long. */
  function swell(score, at) {
    const semis = score.chord[Math.floor(random() * score.chord.length)];
    const osc = context.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = note(semis + 12);
    const gain = context.createGain();
    gain.gain.value = 0;
    const length = 4 + random() * 3;
    gain.gain.setTargetAtTime(0.045, at, length * 0.25);
    gain.gain.setTargetAtTime(0, at + length * 0.55, length * 0.2);
    osc.connect(gain);
    gain.connect(filter);
    osc.start(at);
    osc.stop(at + length * 1.6);
  }

  /** A single soft note, high and short, like a bell heard through a wall. */
  function pick(score, at) {
    const semis = score.chord[Math.floor(random() * score.chord.length)] + 24;
    const osc = context.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = note(semis);
    const gain = context.createGain();
    gain.gain.value = 0;
    gain.gain.setTargetAtTime(0.03, at, 0.02);
    gain.gain.setTargetAtTime(0, at + 0.08, 0.9);
    osc.connect(gain);
    gain.connect(filter);
    osc.start(at);
    osc.stop(at + 4);
  }

  return {
    get score() {
      return current;
    },
    /** Called every frame with the score that should be playing. Cheap when nothing changes. */
    frame(name) {
      const now = context.currentTime;
      const score = SCORE[name] ?? SCORE.backOfHouse;
      if (name !== current) {
        current = name;
        stopDrone(now);
        startDrone(score, now + 0.05);
        filter.frequency.setTargetAtTime(score.bright, now, 2);
        nextSwell = now + 1.5;
        nextPick = now + 3 + random() * 4;
      }
      if (now >= nextSwell) {
        swell(score, now + 0.02);
        nextSwell = now + score.every * (0.7 + random() * 0.6);
      }
      if (now >= nextPick) {
        if (random() < score.pick) pick(score, now + 0.02);
        nextPick = now + 2.5 + random() * 4;
      }
    },
    /** A world sound just played: step back out of its way for a moment. */
    duck(seconds = 1.2) {
      const now = context.currentTime;
      duckGain.gain.cancelScheduledValues(now);
      duckGain.gain.setTargetAtTime(0.5, now, 0.03);
      duckGain.gain.setTargetAtTime(1, now + seconds, 0.6);
    },
    stop() {
      stopDrone(context.currentTime);
      current = null;
    },
  };
}
